import React, { useState } from 'react';
import { formatCurrency, getPlanForAmount } from '@/lib/planConfig';
import { useAuth } from '@/lib/AuthContext';
import { financialAPI } from '@/services/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PlanCard from '@/components/plans/PlanCard';
import ActiveInvestments from '@/components/plans/ActiveInvestments';
import InvestmentSuccessModal from '@/components/plans/InvestmentSuccessModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

// Fetch plans from database via API
const fetchPlans = async () => {
  const data = await financialAPI.getPlans();
  return data.plans || [];
};

export default function Plans() {
  // Função de tradução local (removendo i18n)
  const t = (key, params = {}) => {
    const translations = {
      'plans.title': 'Investment Plans',
      'plans.subtitle': 'Choose a plan and start earning',
      'plans.availableBalance': 'Available Balance',
      'plans.balance': 'Balance',
      'plans.invested': 'Invested',
      'plans.confirmInvestment': 'Confirm Investment',
      'plans.selectedPlan': 'Selected Plan',
      'plans.minAmount': 'Minimum Amount',
      'plans.maxAmount': 'Maximum Amount',
      'plans.investmentAmount': 'Investment Amount',
      'plans.enterAmount': 'Enter amount',
      'plans.cancel': 'Cancel',
      'plans.processing': 'Processing...',
      'plans.confirm': 'Confirm',
      'plans.valueBetween': `Value must be between ${params?.min || ''} and ${params?.max || ''}`,
      'plans.insufficientBalance': `Insufficient balance. Available: ${params?.balance || ''}`,
      'errors.unauthorized': 'Unauthorized',
      'errors.loadPlans': 'Error loading plans'
    };
    return translations[key] || key;
  };

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [successInvestment, setSuccessInvestment] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Fetch plans from database
  const { data: allPlans = [], isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: fetchPlans,
  });
  
  // Filtrar planos: não-admin só vê planos não-liderança
  const plans = allPlans.filter(plan => {
    // Se é plano de liderança e usuário não é admin, esconder
    if (plan.is_leadership && user?.role !== 'admin') {
      return false;
    }
    return true;
  });
  
  // Fetch confirmed deposits to calculate real available balance via API
  const { data: confirmedDeposits = [], error: depositsError } = useQuery({
    queryKey: ['confirmed-deposits', user?.id],
    queryFn: async () => {
      console.log('Fetching confirmed deposits via API for user:', user?.id);
      const transactions = await financialAPI.getTransactions({ type: 'deposit', status: 'confirmed' });
      return transactions.filter(t => t.status === 'confirmed');
    },
    enabled: !!user?.id,
  });
  
  if (depositsError) {
    console.error('Deposits query error:', depositsError);
  }
  
  // Fetch user investments via API
  const { data: investments = [], error: investmentsError } = useQuery({
    queryKey: ['investments', user?.id],
    queryFn: async () => {
      console.log('Fetching investments via API for user:', user?.id);
      const data = await financialAPI.getInvestments();
      return data.investments || [];
    },
    enabled: !!user?.id,
  });
  
  if (investmentsError) {
    console.error('Investments query error:', investmentsError);
  }
  
  // Fetch real balance from API
  const { data: balanceData } = useQuery({
    queryKey: ['balance', user?.id],
    queryFn: async () => {
      return await financialAPI.getBalance();
    },
    enabled: !!user?.id,
  });
  
  const availableBalance = balanceData?.available_balance || 0;
  const totalInvested = investments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  
  console.log('Balance from API:', { availableBalance, totalInvested });
  
  const investMutation = useMutation({
    mutationFn: async (data) => {
      console.log('Creating investment via API with data:', data);
      console.log('User ID:', user?.id);
      console.log('Plan data:', data?.plan);
      
      // Use API to create investment
      const result = await financialAPI.createInvestment({
        plan_slug: data?.plan?.slug || data?.plan?.id || 'basic',
        amount: data?.amount,
        client_share: data?.plan?.client_share || 50,
        company_share: data?.plan?.company_share || 50,
        daily_yield: data?.plan?.base_rate || 0.01,
      });
      
      console.log('Investment created via API:', result);
      return result.investment;
    },
    onSuccess: async (investment) => {
      console.log('Investment mutation success:', investment);
      
      // NOTA: As comissões de rede são geradas no BACKEND (financial.js)
      // durante o POST /investments. Gerar aqui duplicaria as comissões.
      
      setSuccessInvestment(investment);
      setShowSuccessModal(true);
      queryClient.invalidateQueries({ queryKey: ['investments'] });
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['network'] });
      queryClient.invalidateQueries({ queryKey: ['balance'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      setShowDialog(false);
      setSelectedPlan(null);
      setAmount('');
      toast.success('Investment created successfully!');
    },
    onError: (err) => {
      console.error('Investment mutation error:', err);
      console.error('Error message:', err?.message);
      console.error('Error code:', err?.code);
      toast.error(err?.message || 'Error making investment.');
    },
  });

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setAmount('');
    setShowDialog(true);
  };

  const handleInvest = () => {
    const plan = selectedPlan;
    const value = parseFloat(amount);

    if (!value || value < plan.min_amount || value > plan.max_amount) {
      toast.error(t('plans.valueBetween', { min: formatCurrency(plan.min_amount), max: formatCurrency(plan.max_amount) }));
      return;
    }

    if (value > availableBalance) {
      toast.error(t('plans.insufficientBalance', { balance: formatCurrency(availableBalance) }));
      return;
    }

    investMutation.mutate({
      plan: plan,
      amount: value,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('plans.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('plans.subtitle')}
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{t('plans.availableBalance')}</p>
            <p className="text-2xl font-bold text-gold">{formatCurrency(availableBalance)}</p>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <p>{t('plans.balance')}: {formatCurrency(availableBalance)}</p>
            <p>{t('plans.invested')}: {formatCurrency(totalInvested)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={{
              ...plan,
              min: plan.min_amount,
              max: plan.max_amount,
              baseRate: plan.base_rate * 100, // Convert to percentage
              minRate: plan.min_rate * 100,
              maxRate: plan.max_rate * 100,
              clientShare: plan.client_share,
              companyShare: plan.company_share,
              directCommission: plan.direct_commission,
              reinvestmentCommission: plan.reinvestment_commission,
              residualLevels: plan.residual_levels,
              color: plan.color,
              icon: plan.icon,
              isMostPopular: plan.is_most_popular,
              isLeadership: plan.is_leadership,
            }}
            onSelect={() => handleSelectPlan(plan)}
          />
        ))}
      </div>

      <ActiveInvestments />

      <InvestmentSuccessModal
        investment={successInvestment}
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('plans.confirmInvestment')}</DialogTitle>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('plans.selectedPlan')}</p>
                <p className="font-medium">{selectedPlan.name}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">{t('plans.minAmount')}</p>
                <p className="font-medium">{formatCurrency(selectedPlan.min_amount)}</p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">{t('plans.maxAmount')}</p>
                <p className="font-medium">{formatCurrency(selectedPlan.max_amount)}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium">{t('plans.investmentAmount')}</label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={t('plans.enterAmount')}
                  min={selectedPlan.min_amount}
                  max={selectedPlan.max_amount}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('plans.cancel')}
            </Button>
            <Button 
              onClick={handleInvest} 
              disabled={investMutation.isPending}
            >
              {investMutation.isPending ? t('plans.processing') : t('plans.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
